import React, { useEffect, useMemo, useState } from "react";
import { X, Send, Eye, FileText, AlertTriangle, CornerDownLeft, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { sanitizeHtml } from "../lib/sanitize";

const VAR_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

function render(text, ctx) {
  if (!text) return "";
  return text.replace(VAR_RE, (m, key) => {
    const v = ctx[key];
    return v === undefined || v === null || v === "" ? m : String(v);
  });
}

function fmtDate(s) {
  if (!s) return "";
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

function buildReplyDefaults(replyContext) {
  if (!replyContext) return { subject: "", body: "" };
  const orig = replyContext.subject || "(no subject)";
  const subject = /^\s*re\s*:/i.test(orig) ? orig : `Re: ${orig}`;
  const fromLine = replyContext.from_name
    ? `${replyContext.from_name} &lt;${replyContext.from || ""}&gt;`
    : (replyContext.from || "");
  const quoted = replyContext.html ? sanitizeHtml(replyContext.html) : (replyContext.body || "");
  const body = `<p></p><br/><blockquote style="margin:0 0 0 .8ex;border-left:2px solid #d97706;padding-left:1ex;color:#475569"><p style="margin:0 0 6px 0;font-size:12px;color:#64748b">On ${fmtDate(replyContext.at)}, ${fromLine} wrote:</p>${quoted}</blockquote>`;
  return { subject, body };
}

export default function SendEmailDialog({ open, lead, currentUser, onClose, onSent, replyContext }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [toEmail, setToEmail] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [gmailStatus, setGmailStatus] = useState({ connected: false });
  const [me, setMe] = useState(currentUser || null);
  const [attachments, setAttachments] = useState([]);  // template's attachments
  const [selectedAttIds, setSelectedAttIds] = useState(new Set()); // by default all selected

  const onSelectTemplate = async (id) => {
    setTemplateId(id);
    const t = templates.find((x) => x.id === id);
    if (t) {
      setSubject(t.subject);
      setBody(t.body);
    } else {
      setAttachments([]);
      setSelectedAttIds(new Set());
      return;
    }
    try {
      const { data } = await api.get(`/email-templates/${id}/attachments`);
      setAttachments(data);
      setSelectedAttIds(new Set(data.map((a) => a.id)));
    } catch {
      setAttachments([]);
      setSelectedAttIds(new Set());
    }
  };

  useEffect(() => {
    if (!open) return;
    setTemplateId("");
    setShowPreview(false);
    setAttachments([]);
    setSelectedAttIds(new Set());
    const replyDefaults = buildReplyDefaults(replyContext);
    setSubject(replyDefaults.subject);
    setBody(replyDefaults.body);
    // Recipient: if replying, use the original sender; else the lead's POC
    setToEmail(replyContext?.from || lead?.client_poc_email || "");
    (async () => {
      try {
        // Re-fetch the current user so we always have an up-to-date `phone`
        // for {{rep_phone}} substitution in the live preview.
        const [t, g, m] = await Promise.all([
          api.get("/email-templates?active_only=true"),
          api.get("/oauth/gmail/status"),
          api.get("/auth/me"),
        ]);
        setTemplates(t.data);
        setGmailStatus(g.data);
        setMe(m.data);
      } catch (err) {
        console.warn("Failed to load templates/gmail status:", err);
      }
    })();
  }, [open, lead, replyContext]);

  const ctx = useMemo(
    () => ({
      ...(lead || {}),
      rep_name: me?.name,
      rep_email: me?.email,
      rep_phone: me?.phone,
      today: new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }),
    }),
    [lead, me]
  );

  const renderedSubject = useMemo(() => render(subject, ctx), [subject, ctx]);
  const renderedBody = useMemo(() => render(body, ctx), [body, ctx]);

  // Detect unsubstituted vars to warn user
  const unresolved = useMemo(() => {
    const found = new Set();
    [renderedSubject, renderedBody].forEach((s) => {
      let m;
      const re = new RegExp(VAR_RE.source, "g");
      while ((m = re.exec(s))) found.add(m[1]);
    });
    return Array.from(found);
  }, [renderedSubject, renderedBody]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!gmailStatus.connected) {
      toast.error("Connect your Gmail account in Settings first");
      return;
    }
    if (!toEmail.trim()) {
      toast.error("Recipient email is required");
      return;
    }
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject and body are required");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/send-email`, {
        template_id: templateId || null,
        subject,
        body,
        to_email: toEmail,
        thread_id: replyContext?.thread_id || null,
        in_reply_to: replyContext?.message_id || null,
        attachment_ids: templateId ? Array.from(selectedAttIds) : null,
      });
      toast.success(`Sent to ${data.to}`);
      onSent?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="send-email-dialog"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <Send size={15} className="text-orange-500" />
              Send email
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              To {lead?.company_name} {lead?.client_poc_name ? `• ${lead.client_poc_name}` : ""}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100">
            <X size={16} />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          {replyContext && (
            <div
              data-testid="send-email-reply-banner"
              className="bg-orange-50 border border-orange-200 rounded-md p-3 flex items-start gap-2"
            >
              <CornerDownLeft size={15} className="text-orange-600 mt-0.5 shrink-0" />
              <div className="text-xs text-orange-900 min-w-0 flex-1">
                <div className="font-semibold mb-0.5">Replying in thread</div>
                <div className="truncate">
                  To <b>{replyContext.from_name || replyContext.from}</b> — "{replyContext.subject || "(no subject)"}"
                </div>
              </div>
            </div>
          )}
          {!gmailStatus.connected && (
            <div
              data-testid="send-email-gmail-warning"
              className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2"
            >
              <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
              <div className="text-sm text-amber-800">
                <div className="font-semibold mb-0.5">Connect Gmail first</div>
                <div className="text-xs">
                  Go to <a href="/settings" className="underline font-medium">Settings</a> and connect your Gmail
                  account before sending. Emails will be sent from your mailbox.
                </div>
              </div>
            </div>
          )}
          {gmailStatus.connected && (
            <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-1.5 inline-flex items-center gap-1.5">
              Sending from <b className="font-semibold">{gmailStatus.connected_email}</b>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
              Template
            </label>
            <select
              data-testid="send-email-template-select"
              value={templateId}
              onChange={(e) => onSelectTemplate(e.target.value)}
              className="sed-input"
            >
              <option value="">— Start from scratch —</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">To</label>
              <input
                data-testid="send-email-to"
                type="email"
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                className="sed-input"
                placeholder="recipient@example.com"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                From (your Gmail)
              </label>
              <input
                value={gmailStatus.connected_email || me?.email || "(not connected)"}
                readOnly
                className="sed-input bg-slate-50 text-slate-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
              Subject
            </label>
            <input
              data-testid="send-email-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="sed-input"
              placeholder="Re: {{company_name}}"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
                Body (HTML)
              </label>
              <button
                type="button"
                data-testid="send-email-preview-toggle"
                onClick={() => setShowPreview((v) => !v)}
                className="text-xs text-orange-600 hover:text-orange-700 inline-flex items-center gap-1"
              >
                <Eye size={12} /> {showPreview ? "Edit" : "Preview"}
              </button>
            </div>
            {showPreview ? (
              <div
                data-testid="send-email-preview"
                className="border border-slate-200 rounded-md p-4 min-h-[160px] max-h-[300px] overflow-y-auto bg-slate-50 prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderedBody) }}
              />
            ) : (
              <textarea
                data-testid="send-email-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={10}
                className="sed-input font-mono text-xs"
                placeholder="<p>Hi {{client_poc_name}},</p>"
              />
            )}
          </div>

          {attachments.length > 0 && (
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="send-email-attachments">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2 inline-flex items-center gap-1.5">
                <Paperclip size={11} /> Template attachments — uncheck to skip
              </div>
              <ul className="space-y-1.5">
                {attachments.map((a) => {
                  const checked = selectedAttIds.has(a.id);
                  return (
                    <li key={a.id} data-testid={`send-email-attachment-${a.id}`} className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          const next = new Set(selectedAttIds);
                          if (checked) next.delete(a.id); else next.add(a.id);
                          setSelectedAttIds(next);
                        }}
                        className="accent-orange-500"
                        data-testid={`send-email-attachment-toggle-${a.id}`}
                      />
                      <FileText size={12} className="text-orange-600 shrink-0" />
                      <span className={`flex-1 truncate ${checked ? "text-slate-800" : "text-slate-400 line-through"}`}>{a.filename}</span>
                      <span className="text-slate-500 text-[10px] shrink-0">
                        {a.size < 1024 ? `${a.size} B` : a.size < 1024*1024 ? `${(a.size/1024).toFixed(1)} KB` : `${(a.size/1024/1024).toFixed(2)} MB`}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-600">
            <div className="flex items-center gap-1.5 font-semibold text-slate-700 mb-1">
              <FileText size={12} /> Live preview — subject
            </div>
            <div data-testid="send-email-rendered-subject" className="text-slate-900 mb-2">{renderedSubject || <span className="text-slate-400 italic">(empty)</span>}</div>
            {unresolved.length > 0 && (
              <div className="text-amber-700 mt-1" data-testid="send-email-unresolved-warn">
                <AlertTriangle size={11} className="inline mr-1" />
                Unresolved variables: {unresolved.map((v) => `{{${v}}}`).join(", ")}
              </div>
            )}
          </div>
        </div>

        <style>{`.sed-input{width:100%;padding:.5rem .65rem;border:1px solid rgb(226 232 240);border-radius:.375rem;font-size:.85rem;background:white;outline:none}.sed-input:focus{border-color:rgb(249 115 22);box-shadow:0 0 0 2px rgba(249,115,22,.2)}`}</style>

        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-2 bg-slate-50/50">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-md border border-slate-200 bg-white">
            Cancel
          </button>
          <button
            type="submit"
            data-testid="send-email-submit"
            disabled={sending || !gmailStatus.connected}
            className="px-4 py-2 text-sm rounded-md bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-medium inline-flex items-center gap-1.5"
          >
            <Send size={13} /> {sending ? "Sending…" : "Send email"}
          </button>
        </div>
      </form>
    </div>
  );
}
